
import React, { useState, useEffect, Suspense } from 'react';
import { MessageSquare } from 'lucide-react';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import HomePage from './pages/HomePage';
import ProductsPage from './pages/ProductsPage';
import PricingPage from './pages/PricingPage';
import AboutPage from './pages/AboutPage';
import ContactPage from './pages/ContactPage';
import SolarAssistant from './components/SolarAssistant';

export type Page = 'home' | 'products' | 'pricing' | 'about' | 'contact';

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('home');
  const [isAssistantOpen, setIsAssistantOpen] = useState(false);

  // Scroll to top on page change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  const renderPage = () => {
    try {
      switch (currentPage) {
        case 'home': return <HomePage onNavigate={setCurrentPage} />;
        case 'products': return <ProductsPage />;
        case 'pricing': return <PricingPage />;
        case 'about': return <AboutPage />;
        case 'contact': return <ContactPage />;
        default: return <HomePage onNavigate={setCurrentPage} />;
      }
    } catch (err) {
      console.error("Error rendering page:", err);
      return <div className="p-20 text-center">Failed to load page. Please refresh.</div>;
    }
  };

  return (
    <div className="min-h-screen bg-white text-slate-900 font-sans selection:bg-green-100 selection:text-green-900">
      <Navbar currentPage={currentPage} onNavigate={setCurrentPage} />
      
      <main className="animate-in fade-in duration-700">
        <Suspense fallback={<div className="p-20 text-center font-bold">Loading Sun-farms Energy...</div>}>
          {renderPage()}
        </Suspense>
      </main>

      <Footer onNavigate={setCurrentPage} />

      {/* Floating AI Assistant Button */}
      <button 
        onClick={() => setIsAssistantOpen(true)}
        className="fixed bottom-6 right-6 bg-green-700 hover:bg-green-800 text-white p-4 rounded-full shadow-2xl transition-all hover:scale-110 z-50 flex items-center gap-2 group border-4 border-white"
        aria-label="Open Solar Expert Assistant"
      >
        <MessageSquare size={24} />
        <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-300 font-bold uppercase tracking-widest text-xs px-1">
          Solar Expert
        </span>
      </button>

      {/* AI Assistant Modal */}
      {isAssistantOpen && (
        <SolarAssistant onClose={() => setIsAssistantOpen(false)} />
      )}
    </div>
  );
};

export default App;
